import React, { useEffect, useState } from "react";
import YhModal from "../../ui/YhModal";
import YhButton from "../../ui/YhButton";

type Props = {
  open: boolean;
  stageLabel: string;
  onCancel: () => void;
  onConfirm: (reason: string) => void | Promise<void>;
  saving?: boolean;
};

/**
 * Required reason before moving a lead to Not Interested / Lost.
 * Reason is saved as a remark by the caller, then stage is applied.
 */
const StageReasonModal: React.FC<Props> = ({
  open,
  stageLabel,
  onCancel,
  onConfirm,
  saving = false,
}) => {
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setReason("");
      setError("");
    }
  }, [open, stageLabel]);

  const submit = async () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setError("Please enter a reason");
      return;
    }
    await onConfirm(trimmed);
  };

  return (
    <YhModal
      open={open}
      onClose={saving ? undefined : onCancel}
      title={`Reason for ${stageLabel || "stage change"}`}
      width={440}
      closable={!saving}
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-[#6B7280]">
          A reason is required before marking this lead as{" "}
          <span className="font-semibold text-[#111827]">
            {stageLabel || "Not Interested / Lost"}
          </span>
          . It will be saved as a remark.
        </p>
        <textarea
          className="min-h-[96px] w-full resize-y rounded-xl border border-[#E5E7EB] bg-white px-3 py-2.5 text-sm text-[#111827] outline-none transition focus:border-[#841C39] focus:ring-2 focus:ring-[#841C39]/20"
          placeholder="Enter reason…"
          value={reason}
          disabled={saving}
          onChange={(e) => {
            setReason(e.target.value);
            if (error) setError("");
          }}
        />
        {error ? (
          <p className="text-xs font-medium text-red-600">{error}</p>
        ) : null}
        <div className="mt-1 flex justify-end gap-2">
          <YhButton variant="ghost" onClick={onCancel} disabled={saving}>
            Cancel
          </YhButton>
          <YhButton onClick={submit} disabled={saving}>
            {saving ? "Saving…" : "Confirm"}
          </YhButton>
        </div>
      </div>
    </YhModal>
  );
};

export default StageReasonModal;
